"use client"

import { useActionState } from "react"
import { horodatage } from "@/lib/format"
import type { ActionState } from "@/lib/api"
import { FormError, SubmitButton } from "./form"
import { Note } from "./ui"

export type RetractableItem = {
  id: string
  title: string
  quantity: number
  terms: { retractable: boolean; motif: string } | null
  retraction: { possible: boolean; quantity: number; already_requested: number }
}

export function Receipt({ data }: { data: { reference: string; declared_at: string; declaration: string } }) {
  return (
    <div className="grid gap-3" role="status">
      <Note tone="succes">
        <p className="font-bold">Rétractation enregistrée</p>
        <p>Un accusé de réception reprenant votre déclaration vous a été envoyé par e-mail. Conservez-le.</p>
      </Note>
      <dl className="papier p-4 grid gap-2 text-[0.95rem]">
        <div>
          <dt className="text-brun-doux text-sm">Référence</dt>
          <dd className="font-bold">{data.reference}</dd>
        </div>
        <div>
          <dt className="text-brun-doux text-sm">Reçue le</dt>
          <dd>{horodatage(data.declared_at)}</dd>
        </div>
        <div>
          <dt className="text-brun-doux text-sm">Déclaration</dt>
          <dd className="italic">« {data.declaration} »</dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * Formulaire de rétractation : sélection des articles éligibles, identité,
 * puis confirmation explicite. Les articles exclus sont présentés avec leur
 * motif, sans possibilité de sélection.
 */
export function RetractionForm({
  items,
  action,
  hidden = {},
  defaultName = "",
  defaultEmail = "",
}: {
  items: RetractableItem[]
  action: (prev: ActionState, fd: FormData) => Promise<ActionState>
  hidden?: Record<string, string>
  defaultName?: string
  defaultEmail?: string
}) {
  const [state, formAction] = useActionState(action, undefined)
  if (state?.ok && state.data) return <Receipt data={state.data} />
  const eligible = items.filter((i) => i.retraction.possible)
  return (
    <form action={formAction} className="grid gap-5">
      {Object.entries(hidden).map(([k, v]) => (
        <input key={k} type="hidden" name={k} value={v} />
      ))}
      <fieldset className="grid gap-3">
        <legend className="etiquette">Articles concernés</legend>
        {items.map((i) => (
          <div key={i.id} className={`rounded-xl border p-3 ${i.retraction.possible ? "border-ligne bg-white" : "border-ligne bg-creme/40"}`}>
            {i.retraction.possible ? (
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="item" value={i.id} defaultChecked={eligible.length === 1} className="mt-1 w-5 h-5 accent-[#64390e]" />
                <span className="flex-1">
                  <span className="font-bold text-brun">{i.title}</span>
                  {i.retraction.quantity > 1 ? (
                    <span className="mt-2 flex items-center gap-2 text-sm">
                      Quantité :
                      <select name={`qte_${i.id}`} defaultValue={i.retraction.quantity} className="champ !min-h-9 !w-20 !py-1">
                        {Array.from({ length: i.retraction.quantity }, (_, n) => n + 1).map((n) => (
                          <option key={n}>{n}</option>
                        ))}
                      </select>
                    </span>
                  ) : (
                    <input type="hidden" name={`qte_${i.id}`} value="1" />
                  )}
                </span>
              </label>
            ) : (
              <div>
                <p className="font-bold text-brun-doux">{i.title}</p>
                <p className="text-sm text-brun-doux mt-1">
                  {i.terms && !i.terms.retractable
                    ? `Non concerné : ${i.terms.motif}`
                    : i.retraction.already_requested
                      ? "Rétractation déjà déclarée pour cet article."
                      : "Le délai de rétractation est dépassé ou la commande est annulée."}
                </p>
              </div>
            )}
          </div>
        ))}
      </fieldset>
      {eligible.length > 0 ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="r-name" className="etiquette">
                Nom et prénom
              </label>
              <input id="r-name" name="name" required defaultValue={defaultName} className="champ" autoComplete="name" />
            </div>
            <div>
              <label htmlFor="r-email" className="etiquette">
                E-mail pour l’accusé de réception
              </label>
              <input id="r-email" name="email" type="email" required defaultValue={defaultEmail} className="champ" autoComplete="email" />
            </div>
          </div>
          <div>
            <label htmlFor="r-msg" className="etiquette">
              Message (facultatif)
            </label>
            <textarea id="r-msg" name="message" className="champ" />
            <p className="aide">Aucun motif n’est nécessaire.</p>
          </div>
          <label className="flex items-start gap-3 rounded-xl bg-creme/60 p-4 cursor-pointer">
            <input type="checkbox" name="confirmation" value="oui" required className="mt-1 w-5 h-5 accent-[#64390e]" />
            <span>Je confirme me rétracter du contrat pour les articles sélectionnés.</span>
          </label>
          <FormError state={state} />
          <SubmitButton className="bouton bouton-danger w-full sm:w-auto" pending="Enregistrement…">
            Confirmer ma rétractation
          </SubmitButton>
        </>
      ) : (
        <Note tone="neutre">Aucun article de cette commande ne peut faire l’objet d’une rétractation.</Note>
      )}
    </form>
  )
}
